import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle, FileText, Image as ImageIcon, Lock, MapPin, Paperclip,
  PaperPlaneRight, ShieldCheck, Sparkle, Trash, UploadSimple, VideoCamera,
  Warning, X,
} from "@phosphor-icons/react";
import { LogoWordmark } from "@/components/Logo";

const BASE = process.env.REACT_APP_BACKEND_URL;

// Página pública para asegurados/perjudicados. Sin auth, sin Layout. Está
// pensada para móvil primero porque la mayoría de asegurados abrirán el
// enlace desde el WhatsApp.
export default function AseguradoLanding() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    if (!token) return;
    setState({ loading: true, data: null, error: null });
    axios
      .get(`${BASE}/api/public/landing/${token}`)
      .then((r) => setState({ loading: false, data: r.data, error: null }))
      .catch((e) => setState({ loading: false, data: null, error: e?.response?.data?.detail || "Enlace no válido." }));
  }, [token]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-sm text-zinc-500">Cargando...</div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-6">
        <div className="text-center max-w-sm">
          <div className="mx-auto w-12 h-12 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center mb-4">
            <Warning className="w-6 h-6 text-rose-600" weight="fill" />
          </div>
          <h1 className="text-lg font-display font-black tracking-tight text-zinc-900">
            Enlace no disponible
          </h1>
          <p className="text-sm text-zinc-600 mt-2">{state.error}</p>
          <p className="text-xs text-zinc-500 mt-4">
            Contacta con tu perito para que te envíe un nuevo enlace.
          </p>
        </div>
      </div>
    );
  }

  return <LandingContent data={state.data} token={token} />;
}


function LandingContent({ data, token }) {
  const v = data.verificacion || {};
  const isPerjudicado = data.target_type === "perjudicado";
  const branding = data.branding || {};
  const gabineteName = branding.name;
  const gabineteLogoUrl = branding.logo_url ? `${BASE}${branding.logo_url}` : null;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Banda superior con branding del gabinete (logo + nombre) */}
      <header className="bg-[#0E0F17] text-white px-5 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {gabineteLogoUrl ? (
            <img
              src={gabineteLogoUrl}
              alt={gabineteName || "Gabinete"}
              className="h-8 w-auto max-w-[150px] object-contain bg-white rounded-md p-1"
            />
          ) : null}
          {gabineteName && (
            <div className="min-w-0">
              <div className="font-display font-bold text-sm tracking-tight truncate">{gabineteName}</div>
              <div className="text-[10px] text-white/40 truncate">Gabinete pericial</div>
            </div>
          )}
          {!gabineteName && !gabineteLogoUrl && <LogoWordmark light small />}
        </div>
        <span className="text-[10px] uppercase tracking-widest text-white/50 inline-flex items-center gap-1 flex-shrink-0">
          <Lock className="w-3 h-3" weight="bold" /> Espacio privado
        </span>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-6 sm:py-10 space-y-6">
        {/* Bienvenida */}
        <section data-testid="landing-hero">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-900 text-[10px] font-semibold tracking-wider uppercase mb-3">
            <Sparkle className="w-3 h-3" weight="fill" /> {isPerjudicado ? "Comunicación al perjudicado" : "Tu siniestro"}
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-zinc-900 leading-[1.1]">
            {data.title || "Tu siniestro"}
          </h1>
          {data.welcome_message && (
            <p className="text-sm sm:text-base text-zinc-700 mt-3 whitespace-pre-line leading-relaxed">
              {data.welcome_message}
            </p>
          )}
        </section>

        {/* Verificación */}
        <VerificacionCard verificacion={v} isPerjudicado={isPerjudicado} />

        {/* Subir archivos */}
        {data.allow_uploads && (
          <UploadCard
            token={token}
            maxMb={data.max_upload_size_mb}
            maxCount={data.max_uploads}
          />
        )}

        {/* Formulario */}
        {data.form_questions && data.form_questions.length > 0 && (
          <FormCard token={token} questions={data.form_questions} />
        )}

        {/* Mensaje libre */}
        {data.allow_messages && <MessageCard token={token} />}

        <footer className="text-center text-[11px] text-zinc-500 pt-6 pb-10 leading-relaxed">
          <p>
            Este espacio está protegido y sólo lo ve tu perito.<br />
            Si necesitas hablar con él/ella, escríbele aquí mismo.
          </p>
          <p className="mt-3 inline-flex items-center gap-2 justify-center flex-wrap">
            {gabineteName && (
              <span className="font-semibold text-zinc-700">{gabineteName}</span>
            )}
            {branding.web && (
              <a href={branding.web.startsWith("http") ? branding.web : `https://${branding.web}`} target="_blank" rel="noreferrer" className="text-zinc-500 hover:underline">{branding.web}</a>
            )}
            {branding.telefono && (
              <span className="text-zinc-500 font-mono">· {branding.telefono}</span>
            )}
            <span className="text-zinc-400">·</span>
            <span className="text-zinc-400 inline-flex items-center gap-1">gestionado con <LogoWordmark small /></span>
          </p>
        </footer>
      </main>
    </div>
  );
}


function VerificacionCard({ verificacion, isPerjudicado }) {
  return (
    <section
      data-testid="landing-verification"
      className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden"
    >
      <div className="bg-zinc-50 px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-600" weight="fill" />
          <span className="text-xs font-bold uppercase tracking-widest text-zinc-700">Verifica que es tu siniestro</span>
        </div>
      </div>
      <dl className="divide-y divide-zinc-100 text-sm">
        {verificacion.nombre && (
          <Row label={isPerjudicado ? "Perjudicado" : "Asegurado"} value={
            isPerjudicado ? <span><strong>{verificacion.nombre}</strong> <span className="text-zinc-400 font-mono text-xs">·•••••• apellidos</span></span> : <strong>{verificacion.nombre}</strong>
          } />
        )}
        {(verificacion.numero_poliza || verificacion.numero_poliza_masked) && (
          <Row label="Nº Póliza" value={
            <code className="font-mono text-zinc-900">{verificacion.numero_poliza || verificacion.numero_poliza_masked}</code>
          } />
        )}
        {verificacion.numero_siniestro && (
          <Row label="Nº Siniestro" value={<code className="font-mono text-zinc-900">{verificacion.numero_siniestro}</code>} />
        )}
        {verificacion.aseguradora && (
          <Row label="Aseguradora" value={verificacion.aseguradora} />
        )}
        {verificacion.direccion_oculta ? (
          <Row label="Localización" value={
            <span>{[verificacion.municipio, verificacion.provincia].filter(Boolean).join(" · ")} <span className="text-zinc-400 text-xs">(dirección protegida)</span></span>
          } icon={<MapPin className="w-3.5 h-3.5 text-zinc-400" weight="bold" />} />
        ) : (verificacion.direccion || verificacion.municipio) && (
          <Row label="Dirección" value={
            <div>
              <div>{verificacion.direccion}</div>
              <div className="text-zinc-500 text-xs mt-0.5">
                {[verificacion.codigo_postal, verificacion.municipio, verificacion.provincia].filter(Boolean).join(" · ")}
              </div>
            </div>
          } icon={<MapPin className="w-3.5 h-3.5 text-zinc-400" weight="bold" />} />
        )}
      </dl>
    </section>
  );
}

function Row({ label, value, icon }) {
  return (
    <div className="grid grid-cols-3 gap-3 px-5 py-2.5">
      <dt className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500 self-start pt-1 inline-flex items-center gap-1">
        {icon}{label}
      </dt>
      <dd className="col-span-2 text-zinc-900">{value}</dd>
    </div>
  );
}


function UploadCard({ token, maxMb, maxCount }) {
  const [items, setItems] = useState([]); // [{id, file, status, progress, error?, preview?}]
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const onPick = (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const queued = list.map((f) => ({
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      file: f,
      status: "queued",
      progress: 0,
      preview: f.type?.startsWith("image/") ? URL.createObjectURL(f) : null,
    }));
    setItems((prev) => [...prev, ...queued]);
    queued.forEach((q) => uploadOne(q));
  };

  const uploadOne = async (item) => {
    const fd = new FormData();
    fd.append("file", item.file);
    setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, status: "uploading" } : x));
    try {
      await axios.post(`${BASE}/api/public/landing/${token}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (!e.total) return;
          const progress = Math.round((e.loaded / e.total) * 100);
          setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, progress } : x));
        },
      });
      setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, status: "done", progress: 100 } : x));
    } catch (e) {
      setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, status: "error", error: e?.response?.data?.detail || "Error subiendo" } : x));
    }
  };

  return (
    <section
      data-testid="landing-uploads"
      className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden"
    >
      <div className="px-5 py-3 border-b border-zinc-100">
        <h2 className="text-base font-display font-bold tracking-tight text-zinc-900 inline-flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-amber-600" weight="bold" /> Sube fotos, vídeos o documentos
        </h2>
        <p className="text-xs text-zinc-500 mt-1">Cuanto más detallado, más rápido cerraremos tu siniestro. Máx. {maxMb} MB por archivo · {maxCount} archivos.</p>
      </div>

      <div
        className={`m-5 border-2 border-dashed rounded-xl px-4 py-8 text-center transition-all ${
          dragOver ? "border-amber-500 bg-amber-50" : "border-zinc-300 bg-zinc-50/50 hover:border-zinc-400 hover:bg-zinc-50"
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); onPick(e.dataTransfer.files); }}
        onClick={() => fileRef.current?.click()}
        data-testid="landing-upload-dropzone"
      >
        <UploadSimple className="w-7 h-7 mx-auto text-zinc-400 mb-2" weight="bold" />
        <div className="text-sm font-semibold text-zinc-700">Arrastra aquí tus archivos o pulsa para elegir</div>
        <div className="text-[11px] text-zinc-500 mt-1">Imágenes, vídeos, PDF, etc.</div>
        <input
          ref={fileRef}
          type="file"
          multiple
          className="hidden"
          accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx"
          onChange={(e) => { onPick(e.target.files); e.target.value = ""; }}
        />
      </div>

      {items.length > 0 && (
        <ul className="px-5 pb-5 space-y-2" data-testid="landing-upload-list">
          {items.map((it) => (
            <li
              key={it.id}
              data-testid={`landing-upload-item-${it.id}`}
              className="flex items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-lg p-2.5"
            >
              <div className="w-10 h-10 rounded-md bg-white border border-zinc-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                {it.preview ? (
                  <img src={it.preview} alt="" className="w-full h-full object-cover" />
                ) : it.file.type?.startsWith("video/") ? (
                  <VideoCamera className="w-5 h-5 text-zinc-400" weight="bold" />
                ) : it.file.type?.startsWith("image/") ? (
                  <ImageIcon className="w-5 h-5 text-zinc-400" weight="bold" />
                ) : (
                  <FileText className="w-5 h-5 text-zinc-400" weight="bold" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-zinc-900 truncate">{it.file.name}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">{prettyBytes(it.file.size)}</div>
                {it.status === "uploading" && (
                  <div className="mt-1.5 w-full h-1 bg-zinc-200 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 transition-all duration-150" style={{ width: `${it.progress}%` }} />
                  </div>
                )}
              </div>
              <div className="flex-shrink-0">
                {it.status === "done" && <CheckCircle className="w-5 h-5 text-emerald-500" weight="fill" />}
                {it.status === "error" && <span className="text-[11px] text-rose-600">{it.error || "Error"}</span>}
                {it.status === "uploading" && (
                  <span className="text-[11px] font-mono text-zinc-500">{it.progress}%</span>
                )}
                {it.status === "queued" && (
                  <span className="text-[11px] text-zinc-400">En cola</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}


function MessageCard({ token }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    setErr("");
    try {
      await axios.post(`${BASE}/api/public/landing/${token}/message`, { text: t });
      setText("");
      setDone(true);
      setTimeout(() => setDone(false), 4000);
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo enviar.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      data-testid="landing-message"
      className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden"
    >
      <div className="px-5 py-3 border-b border-zinc-100">
        <h2 className="text-base font-display font-bold tracking-tight text-zinc-900 inline-flex items-center gap-2">
          <PaperPlaneRight className="w-4 h-4 text-amber-600" weight="bold" /> Envía un mensaje a tu perito
        </h2>
        <p className="text-xs text-zinc-500 mt-1">Cuéntale lo que ha pasado o lo que necesites. Te responderá en cuanto pueda.</p>
      </div>
      <div className="p-5 space-y-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          placeholder="Hola, me ha pasado..."
          data-testid="landing-message-text"
          className="w-full !text-sm"
        />
        {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
        {done && (
          <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 inline-flex items-center gap-1.5">
            <CheckCircle className="w-3.5 h-3.5" weight="fill" /> Mensaje recibido. Tu perito te contactará pronto.
          </div>
        )}
        <div className="flex justify-end">
          <button
            onClick={send}
            disabled={!text.trim() || sending}
            data-testid="landing-message-send"
            className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-50 transition-colors"
          >
            <PaperPlaneRight className="w-4 h-4" weight="bold" /> Enviar
          </button>
        </div>
      </div>
    </section>
  );
}


function FormCard({ token, questions }) {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setSubmitting(true);
    setErr("");
    try {
      await axios.post(`${BASE}/api/public/landing/${token}/form`, { answers });
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo enviar el formulario.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section className="rounded-2xl border border-emerald-200 bg-emerald-50 shadow-sm p-5 text-center" data-testid="landing-form-done">
        <CheckCircle className="w-6 h-6 text-emerald-600 mx-auto" weight="fill" />
        <div className="text-sm font-bold text-emerald-800 mt-2">Formulario enviado</div>
        <div className="text-xs text-emerald-700 mt-1">Tu perito ya lo recibió. Gracias.</div>
      </section>
    );
  }

  return (
    <section
      data-testid="landing-form"
      className="rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden"
    >
      <div className="px-5 py-3 border-b border-zinc-100">
        <h2 className="text-base font-display font-bold tracking-tight text-zinc-900 inline-flex items-center gap-2">
          <FileText className="w-4 h-4 text-amber-600" weight="bold" /> Información rápida
        </h2>
        <p className="text-xs text-zinc-500 mt-1">Algunas preguntas para acelerar la tramitación.</p>
      </div>
      <div className="p-5 space-y-4">
        {questions.map((q) => (
          <div key={q.id} data-testid={`landing-form-q-${q.id}`}>
            <label className="!normal-case !tracking-normal !text-[12px] !font-semibold !text-zinc-800">
              {q.label}{q.required && <span className="text-rose-600 ml-1">*</span>}
            </label>
            {q.help_text && (
              <div className="text-[11px] text-zinc-500 mb-1">{q.help_text}</div>
            )}
            <FormInput
              q={q}
              value={answers[q.id] ?? ""}
              onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))}
            />
          </div>
        ))}
        {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
        <div className="flex justify-end">
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="landing-form-submit"
            className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-50 transition-colors"
          >
            <CheckCircle className="w-4 h-4" weight="bold" /> Enviar respuestas
          </button>
        </div>
      </div>
    </section>
  );
}

function FormInput({ q, value, onChange }) {
  if (q.type === "textarea") {
    return <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} className="!text-sm" />;
  }
  if (q.type === "number") {
    return <input type="number" value={value} onChange={(e) => onChange(e.target.value)} className="!text-sm" />;
  }
  if (q.type === "date") {
    return <input type="date" value={value} onChange={(e) => onChange(e.target.value)} className="!text-sm" />;
  }
  if (q.type === "yesno") {
    return (
      <div className="flex gap-2 mt-1">
        {["Sí", "No"].map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`text-sm font-semibold px-4 py-1.5 rounded-md border transition-all ${
              value === opt
                ? "bg-amber-600 text-white border-amber-600"
                : "bg-white text-zinc-700 border-zinc-300 hover:border-amber-600"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === "select") {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} className="!text-sm">
        <option value="">— Elige una opción —</option>
        {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className="!text-sm" />;
}


function prettyBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
